import type { CommitOutcome, CrmCommitBody, CrmQueueApi, PreviewResponse, PreviewScope } from './types';

/**
 * The backend caps ONE preview/commit body at 2000 persons
 * (`CrmPreviewInputSchema` / `CrmCommitInputSchema`, both `.max(2000)`), but
 * the no-personIds match-check path buckets the WHOLE conference roster —
 * uncapped — so a large conference can hand back more checked ids than one
 * body may carry. These wrappers keep the wire contract by slicing over-cap
 * requests into cap-sized runs, called sequentially (kind to CRM rate
 * limits; order-stable by construction), and merging the responses back
 * into ONE result so everything downstream still sees a single queue. At or
 * under the cap they pass straight through — one call, response untouched.
 */
export const CRM_BODY_CAP = 2000;

export async function previewInChunks(
  api: CrmQueueApi,
  conferenceId: string,
  source: string,
  personIds: string[],
  // Forwarded to every chunk's `api.preview` call, verbatim, when the caller
  // passes one. Omitted (not passed as `undefined`) when the caller doesn't —
  // `CrmQueueApi.preview`'s own optionality is what makes an omitted `scope`
  // arg and an explicit `undefined` behave identically on the wire, but
  // keeping the call itself 3-arg here too is what the pre-Task-5 tests
  // still pin (`toHaveBeenCalledWith(conferenceId, source, ids)`, no 4th arg).
  scope?: PreviewScope,
): Promise<PreviewResponse> {
  if (personIds.length <= CRM_BODY_CAP) {
    return scope
      ? api.preview(conferenceId, source, personIds, scope)
      : api.preview(conferenceId, source, personIds);
  }

  const responses: PreviewResponse[] = [];
  for (let i = 0; i < personIds.length; i += CRM_BODY_CAP) {
    const chunk = personIds.slice(i, i + CRM_BODY_CAP);
    responses.push(
      await (scope
        ? api.preview(conferenceId, source, chunk, scope)
        : api.preview(conferenceId, source, chunk)),
    );
  }

  return {
    previews: responses.flatMap((r) => r.previews),
    summary: responses
      .map((r) => r.summary)
      .reduce((a, b) => ({
        toCreate: a.toCreate + b.toCreate,
        toUpdate: a.toUpdate + b.toUpdate,
        membershipOnly: a.membershipOnly + b.membershipOnly,
        needsEmail: a.needsEmail + b.needsEmail,
        ambiguous: a.ambiguous + b.ambiguous,
        needsOwner: a.needsOwner + b.needsOwner,
        suggestedOwners: a.suggestedOwners + b.suggestedOwners,
        // `?? 0` because the two repos deploy separately: a portal that
        // ships before the backend meets a summary with no activity terms,
        // and a bare `a + b` would render the whole merged summary as NaN.
        activitiesToPush: (a.activitiesToPush ?? 0) + (b.activitiesToPush ?? 0),
        // Same deploy-skew guard as activitiesToPush above.
        integrationAccountOwned: (a.integrationAccountOwned ?? 0) + (b.integrationAccountOwned ?? 0),
        ownerUnverified: (a.ownerUnverified ?? 0) + (b.ownerUnverified ?? 0),
        collapsed: (a.collapsed ?? 0) + (b.collapsed ?? 0),
        // `ownersUnavailable` is a boolean, not a count: a degraded owners
        // lookup in ANY chunk makes the whole merged summary degraded, so
        // this ORs across chunks rather than summing (`a + b` on booleans
        // would coerce to a meaningless number, not stay a boolean).
        ownersUnavailable: (a.ownersUnavailable ?? false) || (b.ownersUnavailable ?? false),
      })),
    // The tenant default — identical on every chunk of the same connection.
    policy: responses[0].policy,
    // Locked (spec 2026-09-28 §5.8) if ANY chunk says so — a company whose
    // settings forbid overwriting must not show Match unlocked just because
    // an over-cap selection split the request. Omitted entirely (never a
    // bare `true`/`undefined`) when no chunk reports the lock, matching the
    // backend's own "absent means allowed" contract.
    ...(responses.some((r) => r.overwriteAllowed === false) ? { overwriteAllowed: false } : {}),
  };
}

/**
 * A chunk failure propagates — the send reads as failed, nothing is marked
 * spent, exactly like a failed single-body commit. Rows an earlier chunk
 * already landed are safe to re-send: commit re-runs match+diff at write
 * time (spec §5), so they fall out as `changed_since_preview` /
 * membership-only instead of being written blind twice.
 */
export async function commitInChunks(
  api: CrmQueueApi,
  conferenceId: string,
  source: string,
  body: CrmCommitBody,
): Promise<CommitOutcome> {
  if (body.persons.length <= CRM_BODY_CAP) {
    return api.commit(conferenceId, source, body);
  }

  const outcomes: CommitOutcome[] = [];
  for (let i = 0; i < body.persons.length; i += CRM_BODY_CAP) {
    outcomes.push(
      await api.commit(conferenceId, source, {
        ...body,
        persons: body.persons.slice(i, i + CRM_BODY_CAP),
      }),
    );
  }

  return {
    results: outcomes.flatMap((o) => o.results),
    summary: outcomes
      .map((o) => o.summary)
      .reduce((a, b) => ({
        created: a.created + b.created,
        updated: a.updated + b.updated,
        membershipOnly: a.membershipOnly + b.membershipOnly,
        changedSincePreview: a.changedSincePreview + b.changedSincePreview,
        ambiguous: a.ambiguous + b.ambiguous,
        failed: a.failed + b.failed,
        // Same deploy-skew guard as previewInChunks above.
        activitiesLogged: (a.activitiesLogged ?? 0) + (b.activitiesLogged ?? 0),
        activitiesFailed: (a.activitiesFailed ?? 0) + (b.activitiesFailed ?? 0),
        // Lead-rules refusals; absent from older backends, so treat as 0.
        incomplete: (a.incomplete ?? 0) + (b.incomplete ?? 0),
        // `ensureCampaignContainer` is find-or-create — every chunk of a run
        // lands in the same container, so the first chunk's id speaks for all.
        containerId: a.containerId,
      })),
  };
}
